import {
    type AnyRoute,
    createRootRoute,
    createRoute,
    createRouter,
    lazyRouteComponent,
    Outlet,
    redirect as routerRedirect,
    type ErrorComponentProps
} from '@tanstack/react-router';
import { Suspense, type ComponentType, type ReactNode } from 'react';

import { ApiError } from './api';
import { AppRoot } from './app-root';
import { NavigateTo, NavigationSignal } from './navigation';
import { RouteError, RouteNotFound } from './route-states';

// The route tree mirrors apps/webapp/app. Each route loads the webapp's
// page.tsx, layout.tsx, loading.tsx, and error.tsx through `@/app/...`. In
// the browser build, `client/shadow/app/...` at the same path wins (see
// build/webapp-bridge.ts): server components that read data have a client
// version there, and pure layouts and client components are reused as they
// are. Route params keep the webapp's names ($website_id, $post_id), so
// useParams returns what the components expect.

type Module = Record<string, unknown>;
type Loader = () => Promise<Module>;

function lazyDefault(load: Loader) {
    return lazyRouteComponent(
        load as never,
        'default' as never
    ) as ComponentType<{
        children?: ReactNode;
    }>;
}

/** Turns redirect() and notFound() from a page into navigation. */
function signalBoundary(Fallback: ComponentType<ErrorComponentProps>) {
    return function SignalBoundary(props: ErrorComponentProps) {
        const { error } = props;
        // A record the API does not find renders the "Not found" state, as
        // notFound() does in the webapp.
        if (error instanceof ApiError && error.status === 404) {
            return <RouteNotFound />;
        }
        if (error instanceof NavigationSignal) {
            if (error.kind === 'redirect' && error.href) {
                return <NavigateTo href={error.href} />;
            }
            return <RouteNotFound />;
        }
        return <Fallback {...props} />;
    };
}

function errorFrom(load?: Loader) {
    if (!load) return signalBoundary(RouteError);
    const WebappError = lazyRouteComponent(
        load as never,
        'default' as never
    ) as ComponentType<{ error: Error; reset: () => void }>;
    return signalBoundary(({ error, reset }) => (
        <WebappError
            error={error instanceof Error ? error : new Error(String(error))}
            reset={reset}
        />
    ));
}

interface RouteFiles {
    page?: Loader;
    layout?: Loader;
    loading?: Loader;
    error?: Loader;
}

// The parent each route was created under. TanStack sets `parentRoute` only
// when the tree is built, so the groups below cannot read it.
const parents = new Map<AnyRoute, AnyRoute>();
const childrenOf = (parent: AnyRoute, routes: AnyRoute[]) =>
    routes.filter((child) => parents.get(child) === parent);

/** A route that renders a webapp layout and/or page with its loading state. */
function route(
    parent: AnyRoute,
    path: string | { id: string },
    files: RouteFiles
): AnyRoute {
    const Layout = files.layout ? lazyDefault(files.layout) : null;
    const Page = files.page ? lazyDefault(files.page) : null;
    const Loading = files.loading ? lazyDefault(files.loading) : null;
    const body = Page ? <Page /> : <Outlet />;
    const content = (
        <Suspense fallback={Loading ? <Loading /> : null}>{body}</Suspense>
    );
    const created = createRoute({
        getParentRoute: () => parent,
        ...(typeof path === 'string' ? { path } : { id: path.id }),
        component: () => (Layout ? <Layout>{content}</Layout> : content),
        errorComponent: errorFrom(files.error),
        notFoundComponent: RouteNotFound
    });
    parents.set(created, parent);
    return created;
}

const rootRoute = createRootRoute({
    component: () => (
        <AppRoot>
            <Outlet />
        </AppRoot>
    ),
    errorComponent: signalBoundary(RouteError),
    notFoundComponent: RouteNotFound
});

// Signed-out pages: sign-in, sign-up, invites, impersonation, WordPress.
const authLayout = route(
    rootRoute,
    { id: 'auth' },
    {
        layout: () => import('@/app/(auth)/layout')
    }
);
const authPages = [
    route(authLayout, '/signin', {
        page: () => import('@/app/(auth)/signin/page')
    }),
    route(authLayout, '/signup', {
        page: () => import('@/app/(auth)/signup/page')
    }),
    route(authLayout, '/sign-in', {
        page: () => import('@/app/(auth)/sign-in/page')
    }),
    route(authLayout, '/login', {
        page: () => import('@/app/(auth)/login/page')
    }),
    route(authLayout, '/invite/$token', {
        page: () => import('@/app/(auth)/invite/[token]/page')
    }),
    route(authLayout, '/impersonate/$token', {
        page: () => import('@/app/(auth)/impersonate/[token]/page')
    }),
    route(authLayout, '/auth/wordpress/consent', {
        page: () => import('@/app/(auth)/auth/wordpress/consent/page')
    })
];

// Signed-in pages. AppRoot sends signed-out visitors to /signin first.

const authenticated = route(
    rootRoute,
    { id: 'authenticated' },
    {
        layout: () => import('@/app/(authenticated)/layout')
    }
);

const appShell = route(
    authenticated,
    { id: 'app-shell' },
    {
        layout: () => import('@/app/(authenticated)/(app-shell)/layout'),
        error: () => import('@/app/(authenticated)/(app-shell)/error')
    }
);

const websitesList = route(
    appShell,
    { id: 'websites-list' },
    {
        layout: () =>
            import('@/app/(authenticated)/(app-shell)/websites/(list)/layout')
    }
);
const websitesSettings = route(appShell, '/websites/settings', {
    layout: () =>
        import('@/app/(authenticated)/(app-shell)/websites/settings/layout'),
    page: () =>
        import('@/app/(authenticated)/(app-shell)/websites/settings/page'),
    loading: () =>
        import('@/app/(authenticated)/(app-shell)/websites/settings/loading')
});
const managed = route(
    appShell,
    { id: 'managed' },
    {
        layout: () => import('@/app/(authenticated)/(app-shell)/managed/layout')
    }
);

const website = route(appShell, '/websites/$website_id', {
    layout: () =>
        import('@/app/(authenticated)/(app-shell)/websites/[website_id]/layout'),
    loading: () =>
        import('@/app/(authenticated)/(app-shell)/websites/[website_id]/loading')
});
const memory = route(
    website,
    { id: 'business-memory' },
    {
        layout: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/layout')
    }
);
const articles = route(
    website,
    { id: 'articles' },
    {
        layout: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/layout')
    }
);
const articleSettings = route(
    articles,
    { id: 'article-settings' },
    {
        layout: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/settings/layout')
    }
);
const aiVisibility = route(
    website,
    { id: 'ai-visibility' },
    {
        layout: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/layout'),
        error: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/error')
    }
);

const websitePages = [
    route(website, '/', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/loading')
    }),
    route(memory, '/business-memory', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/loading')
    }),
    route(memory, '/business-memory/brand', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/brand/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/brand/loading')
    }),
    route(memory, '/business-memory/competitors', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/competitors/(list)/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/competitors/(list)/loading')
    }),
    route(memory, '/business-memory/competitors/$competitor_id', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/competitors/[competitor_id]/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/competitors/[competitor_id]/loading')
    }),
    route(memory, '/business-memory/customers', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/customers/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/customers/loading')
    }),
    route(memory, '/business-memory/products', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/products/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/business-memory/products/loading')
    }),
    route(website, '/content/planner', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/planner/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/planner/loading')
    }),
    route(articles, '/content/posts', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/(list)/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/(list)/loading')
    }),
    route(articles, '/content/posts/categories', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/categories/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/categories/loading')
    }),
    route(articleSettings, '/content/posts/settings', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/settings/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/settings/loading')
    }),
    route(articleSettings, '/content/posts/settings/structure', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/settings/structure/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/settings/structure/loading')
    }),
    route(articleSettings, '/content/posts/settings/writing', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/settings/writing/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/(articles)/settings/writing/loading')
    }),
    route(website, '/content/posts/$post_id', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/[post_id]/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/posts/[post_id]/loading')
    }),
    route(website, '/content/strategy', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/strategy/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/content/strategy/loading')
    }),
    route(website, '/publishing', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/publishing/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/publishing/loading')
    }),
    route(website, '/publishing/webhooks', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/publishing/webhooks/page')
    }),
    route(aiVisibility, '/seo/ai-visibility', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/loading')
    }),
    route(aiVisibility, '/seo/ai-visibility/bots', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/bots/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/loading')
    }),
    route(aiVisibility, '/seo/ai-visibility/visitors', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/visitors/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/loading')
    }),
    route(aiVisibility, '/seo/ai-visibility/pages', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/pages/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/pages/loading')
    }),
    route(aiVisibility, '/seo/ai-visibility/settings', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/settings/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/ai-visibility/settings/loading')
    }),
    route(website, '/seo/backlinks', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/backlinks/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/backlinks/loading'),
        error: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/backlinks/error')
    }),
    route(website, '/seo/search-console', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/search-console/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/search-console/loading'),
        error: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/seo/search-console/error')
    }),
    route(website, '/settings/blog', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/settings/blog/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/settings/blog/loading')
    }),
    route(website, '/settings/members', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/settings/members/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/settings/members/loading')
    }),
    route(website, '/settings/slack', {
        page: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/settings/slack/page'),
        loading: () =>
            import('@/app/(authenticated)/(app-shell)/websites/[website_id]/settings/slack/loading')
    })
];

const focused = route(
    authenticated,
    { id: 'focused' },
    {
        layout: () => import('@/app/(authenticated)/(focused)/layout'),
        error: () => import('@/app/(authenticated)/(focused)/error')
    }
);
const focusedPages = [
    route(focused, '/accounts/admins', {
        page: () =>
            import('@/app/(authenticated)/(focused)/accounts/admins/page'),
        loading: () =>
            import('@/app/(authenticated)/(focused)/accounts/admins/loading')
    }),
    route(focused, '/accounts/billing', {
        page: () =>
            import('@/app/(authenticated)/(focused)/accounts/billing/page'),
        loading: () =>
            import('@/app/(authenticated)/(focused)/accounts/billing/loading')
    }),
    route(focused, '/accounts/billing/$websiteId', {
        page: () =>
            import('@/app/(authenticated)/(focused)/accounts/billing/[websiteId]/page')
    }),
    route(focused, '/accounts/integrations/slack/connect', {
        page: () =>
            import('@/app/(authenticated)/(focused)/accounts/integrations/slack/connect/page')
    }),
    route(focused, '/accounts/integrations/slack/link/$token', {
        page: () =>
            import('@/app/(authenticated)/(focused)/accounts/integrations/slack/link/[token]/page')
    }),
    route(focused, '/accounts/integrations/webflow/connect', {
        page: () =>
            import('@/app/(authenticated)/(focused)/accounts/integrations/webflow/connect/page')
    }),
    route(focused, '/accounts/notifications', {
        page: () =>
            import('@/app/(authenticated)/(focused)/accounts/notifications/page'),
        loading: () =>
            import('@/app/(authenticated)/(focused)/accounts/notifications/loading')
    }),
    route(focused, '/resubscribe', {
        page: () => import('@/app/(authenticated)/(focused)/resubscribe/page')
    }),
    route(focused, '/subscribe', {
        page: () => import('@/app/(authenticated)/(focused)/subscribe/page')
    }),
    route(focused, '/subscribe/success', {
        page: () =>
            import('@/app/(authenticated)/(focused)/subscribe/success/page')
    }),
    route(focused, '/trial', {
        page: () => import('@/app/(authenticated)/(focused)/trial/page')
    }),
    route(focused, '/websites/new', {
        page: () => import('@/app/(authenticated)/(focused)/websites/new/page')
    }),
    route(focused, '/websites/new/trial-gate', {
        page: () =>
            import('@/app/(authenticated)/(focused)/websites/new/trial-gate/page')
    })
];

const home = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: () => {
        throw routerRedirect({ to: '/websites', replace: true });
    }
});

// The members page moved to /accounts/admins (next.config.js redirects).
const accountMembers = createRoute({
    getParentRoute: () => rootRoute,
    path: '/accounts/members',
    beforeLoad: () => {
        throw routerRedirect({ href: '/accounts/admins', replace: true });
    }
});

const routeTree = rootRoute.addChildren([
    home,
    accountMembers,
    authLayout.addChildren(authPages),
    authenticated.addChildren([
        appShell.addChildren([
            websitesList.addChildren([
                route(websitesList, '/websites', {
                    page: () =>
                        import('@/app/(authenticated)/(app-shell)/websites/(list)/page'),
                    loading: () =>
                        import('@/app/(authenticated)/(app-shell)/websites/(list)/loading')
                })
            ]),
            websitesSettings,
            managed.addChildren([
                route(managed, '/managed/apply', {
                    page: () =>
                        import('@/app/(authenticated)/(app-shell)/managed/apply/page'),
                    loading: () =>
                        import('@/app/(authenticated)/(app-shell)/managed/apply/loading')
                })
            ]),
            website.addChildren([
                memory.addChildren(childrenOf(memory, websitePages)),
                aiVisibility.addChildren(
                    childrenOf(aiVisibility, websitePages)
                ),
                articles.addChildren([
                    ...childrenOf(articles, websitePages),
                    articleSettings.addChildren(
                        childrenOf(articleSettings, websitePages)
                    )
                ]),
                ...childrenOf(website, websitePages)
            ])
        ]),
        focused.addChildren(focusedPages)
    ])
]);

export const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    // Search params stay strings, as in Next.js: pages read them with
    // useSearchParams.
    parseSearch: (search) =>
        Object.fromEntries(new URLSearchParams(search)) as Record<
            string,
            string
        >,
    stringifySearch: (search) => {
        const params = new URLSearchParams(search as Record<string, string>);
        const text = params.toString();
        return text ? `?${text}` : '';
    }
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}
